import { AppError } from '@shared/api/error'

export type UserFacingError = {
  title: string
  description: string
}

export type UserFacingErrorOptions = {
  title?: string
  fallback?: string
  context?: 'product-save'
}

const statusMessages: Record<number, UserFacingError> = {
  401: {
    title: 'Sessão expirada',
    description: 'Entre novamente para continuar.',
  },
  403: {
    title: 'Acesso não permitido',
    description: 'Você não tem permissão para realizar esta ação.',
  },
  404: {
    title: 'Não encontramos o conteúdo',
    description: 'Atualize a tela e tente novamente.',
  },
  409: {
    title: 'Não foi possível concluir',
    description: 'O estado mudou. Atualize a tela e tente novamente.',
  },
  422: {
    title: 'Revise os dados',
    description: 'Confira os campos destacados e tente novamente.',
  },
  429: {
    title: 'Muitas tentativas',
    description: 'Aguarde alguns instantes antes de tentar novamente.',
  },
  500: {
    title: 'Serviço indisponível',
    description: 'Tente novamente em alguns instantes.',
  },
  502: {
    title: 'Serviço indisponível',
    description: 'Tente novamente em alguns instantes.',
  },
  503: {
    title: 'Serviço indisponível',
    description: 'Tente novamente em alguns instantes.',
  },
  504: {
    title: 'Serviço indisponível',
    description: 'Tente novamente em alguns instantes.',
  },
}

const codeMessages: Record<string, UserFacingError> = {
  EnrollmentProfileError: {
    title: 'Revise a identidade do perfil',
    description:
      'Complete ou corrija o CPF/CNPJ e o tipo de pessoa no cadastro do perfil antes de criar a conta.',
  },
  MissingTermsAcceptanceError: {
    title: 'Aceite não encontrado',
    description:
      'Esta conta não tem o aceite exigido no cadastro. Crie uma nova conta VendePay para continuar.',
  },
  PrincipalIdentityLockedError: {
    title: 'Identidade principal já definida',
    description:
      'CPF/CNPJ e tipo de pessoa não podem ser alterados. Para usar outra identidade, crie uma nova conta VendePay.',
  },
  StaleTermsVersionError: {
    title: 'Termos atualizados',
    description: 'Leia a versão atual dos termos e marque o aceite novamente.',
  },
  CpfAlreadyRegisteredError: {
    title: 'CPF já cadastrado',
    description:
      'Este CPF já está cadastrado em outra conta. Revise o documento ou entre na sua conta.',
  },
  CnpjAlreadyRegisteredError: {
    title: 'CNPJ já cadastrado',
    description:
      'Este CNPJ já está cadastrado em outra conta. Revise o documento ou entre na sua conta.',
  },
  PhoneAlreadyRegisteredError: {
    title: 'Telefone já cadastrado',
    description: 'Este telefone já está cadastrado em outra conta. Use outro telefone.',
  },
  PartnershipRoleConflictError: {
    title: 'Parcerias incompatíveis',
    description:
      'O mesmo usuário não pode ser afiliado aprovado e coprodutor ativo do mesmo produto. Encerre o vínculo ativo antes de aprovar a outra parceria.',
  },
  AffiliateDocumentationNotApprovedError: {
    title: 'Sua documentação ainda não foi aprovada',
    description:
      'Para se afiliar, acesse o cadastro de recebimento e conclua as pendências. Se já enviou os documentos, aguarde a aprovação e tente novamente.',
  },
  ProductNotFound: {
    title: 'Não encontramos o produto',
    description: 'Atualize a tela e tente novamente.',
  },
  Unauthorized: statusMessages[401]!,
  Forbidden: statusMessages[403]!,
  Conflict: statusMessages[409]!,
  ValidationError: statusMessages[422]!,
  InternalServerError: statusMessages[500]!,
  ServiceUnavailable: statusMessages[503]!,
}

const defaultMessage: UserFacingError = {
  title: 'Algo deu errado',
  description: 'Não conseguimos concluir esta ação. Tente novamente.',
}

function messageFor(error: unknown): UserFacingError {
  if (!(error instanceof AppError)) return defaultMessage
  return codeMessages[error.code] ?? statusMessages[error.status] ?? defaultMessage
}

const funnelNodeDescriptions: Record<string, string> = {
  'offerId duplicado no funil':
    'Esta oferta já está em outra etapa do funil. Selecione outra oferta ou remova a etapa que usa a mesma oferta.',
  'id duplicado':
    'Uma etapa está repetida no funil. Remova a etapa duplicada e adicione-a novamente antes de salvar.',
  'raiz deve usar branch accept':
    'A primeira etapa do funil precisa seguir a compra aprovada. Reorganize o funil para iniciar por esse caminho.',
  'parentId deve referenciar outro node ou product':
    'Uma etapa está ligada a uma etapa que não existe mais. Reorganize o funil e conecte-a a uma etapa existente.',
  'so pode existir um filho por parentId e branch':
    'Há duas ofertas no mesmo caminho de aceite ou recusa. Mantenha apenas uma próxima etapa por caminho.',
  'so pode existir uma raiz de funil':
    'O funil tem mais de uma etapa inicial. Mantenha apenas uma etapa ligada diretamente à compra do produto principal.',
  'offerId deve pertencer ao mesmo produtor':
    'A oferta selecionada pertence a outro produtor. Selecione uma oferta de um produto seu.',
  'planId inexistente':
    'O plano selecionado não foi encontrado. Selecione novamente um plano disponível para essa oferta.',
  'planId deve pertencer ao produto da oferta':
    'O plano selecionado pertence a outro produto. Selecione um plano do produto usado nessa etapa.',
  'price display-only deve bater com value do plano':
    'O preço da etapa não corresponde ao valor do plano selecionado. Selecione novamente o plano para atualizar o preço.',
  'price deve ser > 0':
    'O preço do upsell ou downsell precisa ser maior que zero. Corrija o valor da etapa antes de salvar.',
  'externalPageUrl deve ficar vazio no modo hosted':
    'A etapa usa uma página hospedada pela plataforma, mas tem uma URL externa preenchida. Remova a URL ou altere para página própria.',
  'externalPageUrl deve ser uma URL absoluta http ou https':
    'A URL da página própria está incompleta ou inválida. Informe o endereço completo, começando com https:// ou http://.',
  'externalPageUrl deve ser uma URL absoluta HTTPS':
    'A URL da página própria está incompleta ou inválida. Informe o endereço completo, começando com https://.',
  'externalPageUrl não pode conter fragmento (#)':
    'A URL da página própria não pode conter #. Remova o # e o trecho que vem depois dele antes de salvar.',
}

function productSaveDescription(error: unknown): string | undefined {
  if (!(error instanceof AppError) || error.status !== 422) return undefined

  const isOrderBump = error.code === 'InvalidOrderBumpConfigError'
  const isFunnelNode = error.code === 'InvalidFunnelNodeConfigError'
  if (!isOrderBump && !isFunnelNode) return undefined

  // The Core envelope exposes the reason only in message. Match known reasons,
  // but never render the original message, identifiers or request metadata.
  const detail = error.message.split(' invalido: ')[1]
  const targetField = isOrderBump ? 'targetOfferId' : 'offerId'
  const stage = isOrderBump ? 'order bump' : 'upsell ou downsell'

  if (detail === `${targetField} deve apontar para produto ACTIVE`) {
    return `O produto selecionado no ${stage} não está publicado. Publique esse produto ou selecione outro produto publicado e salve novamente.`
  }
  if (detail === `${targetField} deve apontar para oferta ACTIVE`) {
    return `A oferta selecionada no ${stage} não está ativa. Selecione uma oferta ativa de um produto publicado e salve novamente.`
  }
  if (isFunnelNode && detail) {
    if (Object.hasOwn(funnelNodeDescriptions, detail)) return funnelNodeDescriptions[detail]
    if (/^price deve ser >= \d+(?:\.\d+)? [A-Z]{3}$/.test(detail)) {
      return 'O preço do upsell ou downsell está abaixo do mínimo permitido. Revise o preço da etapa na moeda base do produto antes de salvar.'
    }
    if (
      /^preco base deve usar [A-Z]{3}; [A-Z]{3} deve ser configurado como moeda de apresentacao$/.test(
        detail,
      )
    ) {
      return 'O preço do upsell ou downsell precisa usar a moeda base do produto principal. Use essa moeda na etapa e configure outras moedas como moedas de apresentação.'
    }
  }
  if (isOrderBump && detail === 'ownerProductId deve apontar para produto ACTIVE') {
    return 'O produto principal não está publicado. Publique-o antes de salvar o order bump.'
  }

  return `Não foi possível salvar o ${stage}. Revise o produto, a oferta e as configurações dessa etapa e tente novamente.`
}

export function getUserFacingError(
  error: unknown,
  options: UserFacingErrorOptions = {},
): UserFacingError {
  const message = messageFor(error)
  const contextualDescription =
    options.context === 'product-save' ? productSaveDescription(error) : undefined
  return {
    title: options.title ?? message.title,
    description:
      contextualDescription ??
      (error instanceof AppError &&
      [
        'PartnershipRoleConflictError',
        'EnrollmentProfileError',
        'MissingTermsAcceptanceError',
        'PrincipalIdentityLockedError',
        'StaleTermsVersionError',
      ].includes(error.code)
        ? message.description
        : (options.fallback ?? message.description)),
  }
}

export function getUserFacingErrorMessage(error: unknown, fallback: string): string {
  return getUserFacingError(error, { fallback }).description
}

/** A payout failure is an observed state, not an HTTP error. Ignore the provider message. */
export function getPayoutFailureFeedback(code: string | null): UserFacingError {
  const descriptions: Readonly<Record<string, string>> = {
    account_closed:
      'A conta bancária foi encerrada. Cadastre outra conta para os próximos repasses.',
    account_frozen: 'A conta bancária está bloqueada. Confira a situação com seu banco.',
    bank_account_restricted:
      'A conta bancária tem uma restrição. Confira a situação com seu banco.',
    bank_ownership_changed:
      'O titular da conta bancária mudou. Confira o titular e cadastre uma conta válida.',
    invalid_account_number:
      'O número da conta bancária não é válido. Corrija os dados no cadastro.',
    incorrect_account_holder_name:
      'O nome do titular não corresponde à conta bancária. Confira os dados no cadastro.',
    invalid_currency: 'A conta bancária não aceita a moeda do repasse. Confira a conta cadastrada.',
    no_account: 'A conta bancária não foi encontrada. Confira os dados no cadastro.',
    debit_not_authorized: 'O banco não autorizou a operação. Confira a situação com seu banco.',
    insufficient_funds: 'Não havia saldo suficiente para este repasse. Acompanhe as atualizações.',
    could_not_process:
      'O banco não conseguiu processar este repasse. Confira a situação com seu banco.',
  }
  return {
    title: 'O repasse apresentou uma falha',
    description:
      code && Object.hasOwn(descriptions, code)
        ? descriptions[code]!
        : 'Confira os dados de recebimento no seu cadastro. Se precisar, entre em contato com o suporte.',
  }
}
